import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Upload, Scan, CheckCircle, AlertCircle, Info } from 'lucide-react';

export default function ScanDocument() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentType, setDocumentType] = useState('');
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState(null);

  const subscription = user?.subscription || {};
  const scansUsed = subscription.ai_scans_used_month || 0;
  const scansLimit = subscription.max_ai_scans_per_month || 3;
  const isUnlimited = scansLimit === -1;
  const scansRemaining = isUnlimited ? '∞' : Math.max(0, scansLimit - scansUsed);

  const documentTypes = [
    { 
      value: 'driving_licence', 
      label: '🪪 UK Driving Licence', 
      description: 'Photocard driving licence',
      fields: 'Name, DOB, licence number, address, categories'
    },
    { 
      value: 'v5c', 
      label: '📄 V5C Registration', 
      description: 'Vehicle logbook',
      fields: 'Registration, VIN, make, model, year, color, fuel type'
    },
    { 
      value: 'insurance', 
      label: '🛡️ Insurance Certificate', 
      description: 'Motor insurance document',
      fields: 'Policy number, insurer, cover dates, vehicle details'
    },
    { 
      value: 'mot', 
      label: '✅ MOT Certificate', 
      description: 'MOT test certificate',
      fields: 'Test date, expiry, mileage, defects/advisories'
    },
    { 
      value: 'receipt', 
      label: '🧾 Service Receipt', 
      description: 'Garage receipt/invoice',
      fields: 'Date, garage, mileage, work done, cost'
    },
  ];

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (JPEG, PNG, or WEBP)');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large (max 10MB)');
      return;
    }

    setSelectedFile(file);
    setResult(null);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleScan = async () => {
    if (!selectedFile || !documentType) {
      toast.error('Please select a file and document type');
      return;
    }

    setScanning(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('document_type', documentType);

      const response = await apiClient.post('/documents/scan', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setResult(response.data);
      toast.success('Document scanned successfully! ✨', { duration: 3000 });
      
      // If it's a V5C, offer to create vehicle
      if (documentType === 'v5c') {
        toast.success('Vehicle details extracted! Click "Add to Garage" below.', {
          duration: 5000
        });
      }
      
    } catch (error) {
      console.error('Scan error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to scan document. Please ensure the image is clear and the document is fully visible.';
      toast.error(errorMsg, { duration: 5000 });
    } finally {
      setScanning(false);
    }
  };

  const handleCreateVehicle = async (scanId) => {
    try {
      const formData = new FormData();
      formData.append('scan_id', scanId);

      const vehicle = await apiClient.post('/vehicles/create-from-scan', formData);
      
      toast.success('Vehicle added to your garage! 🚗');
      navigate('/vehicles');
    } catch (error) {
      console.error('Create vehicle error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create vehicle');
    }
  };

  const selectedDocType = documentTypes.find(t => t.value === documentType);

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="scan-document-page">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <Scan className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold">Scan Document</h1>
            <p className="text-muted-foreground">AI-powered data extraction from vehicle documents</p>
          </div>
        </div>
        
        {/* Usage Badge */}
        <div className="flex flex-col items-end gap-1">
          <Badge 
            variant={scansRemaining === 0 ? 'destructive' : 'secondary'}
            className="text-sm"
            data-testid="scans-remaining-badge"
          >
            {isUnlimited ? 'Unlimited Scans' : `${scansRemaining} Scans Left`}
          </Badge>
          {!isUnlimited && (
            <p className="text-xs text-muted-foreground">
              {scansUsed}/{scansLimit} used this month
            </p>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Upload Document</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Document Type Selection */}
          <div>
            <label className="block text-sm font-medium mb-2">Document Type</label>
            <Select value={documentType} onValueChange={setDocumentType}>
              <SelectTrigger data-testid="document-type-select" className="w-full">
                <SelectValue placeholder="Select document type" />
              </SelectTrigger>
              <SelectContent>
                {documentTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="py-1">
                      <div className="font-medium">{type.label}</div>
                      <div className="text-xs text-muted-foreground">{type.description}</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedDocType && (
              <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <p className="text-sm text-blue-900 dark:text-blue-100">
                  <Info className="w-4 h-4 inline mr-1" />
                  <strong>Will extract:</strong> {selectedDocType.fields}
                </p>
              </div>
            )}
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium mb-2">Upload Image</label>
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center hover:border-primary transition-colors">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload"
                data-testid="file-upload-input"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                {preview ? (
                  <div className="space-y-4">
                    <img 
                      src={preview} 
                      alt="Preview" 
                      className="max-h-80 mx-auto rounded-lg shadow-md border border-gray-200 dark:border-gray-700" 
                    />
                    <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById('file-upload').click();
                      }}
                    >
                      Change Image
                    </Button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <p className="text-lg font-medium">Click to upload or drag and drop</p>
                    <p className="text-sm text-muted-foreground mt-2">
                      JPEG, PNG, or WEBP (max 10MB)
                    </p>
                  </>
                )}
              </label>
            </div>
          </div>

          {/* Scan Button */}
          <Button
            onClick={handleScan}
            disabled={!selectedFile || !documentType || scanning}
            className="w-full"
            size="lg"
            data-testid="scan-button"
          >
            {scanning ? (
              <>
                <Scan className="w-5 h-5 mr-2 animate-spin" />
                Scanning Document with AI...
              </>
            ) : (
              <>
                <Scan className="w-5 h-5 mr-2" />
                Scan Document
              </>
            )}
          </Button>

          {/* Results */}
          {result && (
            <div className="mt-6 p-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-start gap-3 mb-4">
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-green-900 dark:text-green-100 text-lg">
                    Scan Successful!
                  </h3>
                  <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                    Data extracted with high confidence using GPT-5.2 Vision AI
                  </p>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 mt-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  Extracted Data:
                </h4>
                <div className="space-y-2">
                  {Object.entries(result.extracted_data).map(([key, value]) => {
                    if (value === null || value === undefined) return null;
                    
                    // Format key for display
                    const displayKey = key.split('_').map(word => 
                      word.charAt(0).toUpperCase() + word.slice(1)
                    ).join(' ');
                    
                    // Handle nested objects/arrays
                    const displayValue = typeof value === 'object' 
                      ? JSON.stringify(value, null, 2) 
                      : value.toString();
                    
                    return (
                      <div key={key} className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700 last:border-0">
                        <span className="font-medium text-sm">{displayKey}:</span>
                        <span className="text-sm text-muted-foreground text-right ml-4">
                          {displayValue.length > 50 ? displayValue.substring(0, 50) + '...' : displayValue}
                        </span>
                      </div>
                    );
                  })}
                </div>
                
                <details className="mt-4">
                  <summary className="text-sm text-muted-foreground cursor-pointer hover:text-foreground">
                    View Full JSON Data
                  </summary>
                  <pre className="text-xs overflow-auto max-h-96 bg-gray-50 dark:bg-gray-900 p-3 rounded mt-2">
                    {JSON.stringify(result.extracted_data, null, 2)}
                  </pre>
                </details>
              </div>

              {documentType === 'v5c' && (
                <Button
                  onClick={() => handleCreateVehicle(result.scan_id)}
                  className="w-full mt-4"
                  data-testid="create-vehicle-button"
                >
                  Add Vehicle to Garage 🚗
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Help Card */}
      <Card>
        <CardContent className="pt-6">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Info className="w-5 h-5 text-primary" />
            Tips for Best Results
          </h3>
          <ul className="text-sm text-muted-foreground space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span><strong>Good lighting:</strong> Ensure even lighting, avoid shadows and glare</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span><strong>Full document:</strong> Capture the entire document in frame</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span><strong>Keep flat:</strong> Document should be flat and in focus</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span><strong>Plain background:</strong> Use a plain background if possible</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span><strong>Sharp image:</strong> Avoid blurry or heavily skewed images</span>
            </li>
          </ul>
          
          <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <p className="text-sm text-amber-900 dark:text-amber-100">
              <AlertCircle className="w-4 h-4 inline mr-1" />
              <strong>Privacy:</strong> Your documents are processed securely and stored encrypted. You can delete scans anytime.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
